import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import { AUDIO_ACCEPT, isAudioFile, isIOS } from './audioInput';

const PUBLIC_API_URL = `${process.env.REACT_APP_API_URL}/public`;

type Step = 'loading' | 'closed' | 'form' | 'uploading' | 'done' | 'error';

// ReCap meeting purpose: enables the operational warnings (no explicit progress /
// closure) only for decision-making meetings. Labels mirror recap.purpose.* in it.json;
// this page is Italian-only like the rest of its copy.
const MEETING_PURPOSE_OPTIONS: { value: string; label: string }[] = [
  { value: 'unspecified', label: 'Non indicato' },
  { value: 'decisional', label: 'Decisionale / operativo' },
  { value: 'exploratory', label: 'Esplorativo' },
  { value: 'informative', label: 'Informativo' },
  { value: 'other', label: 'Altro' },
];

const publicFetch = async (body: Record<string, unknown>): Promise<unknown> => {
  const res = await fetch(PUBLIC_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? 'Request failed');
  return json.output;
};

interface Props {
  sessionToken: string;
}

export const UploadPage: React.FC<Props> = ({ sessionToken }) => {
  const [step, setStep] = React.useState<Step>('loading');
  const [sessionLabel, setSessionLabel] = React.useState('');
  const [passcode, setPasscode] = React.useState('');
  const [groupName, setGroupName] = React.useState('');
  const [numParticipants, setNumParticipants] = React.useState('');
  const [meetingPurpose, setMeetingPurpose] = React.useState('unspecified');
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  const [sessionType, setSessionType] = React.useState<'managed' | 'self-service'>('managed');
  const [reportEmail, setReportEmail] = React.useState('');

  React.useEffect(() => {
    publicFetch({ operation: 'public_get_session_info', sessionToken })
      .then((out) => {
        const { label, status, type } = out as { label: string; status: string; type: string };
        setSessionLabel(label);
        setSessionType(type === 'self-service' ? 'self-service' : 'managed');
        setStep(status === 'closed' ? 'closed' : 'form');
      })
      .catch(() => setStep('error'));
  }, [sessionToken]);

  const handleSubmit = async () => {
    if (!file || !groupName || !numParticipants || !passcode) return;
    setStep('uploading');
    setErrorMsg('');
    try {
      const { uploadUrl, fileKey } = (await publicFetch({
        operation: 'public_get_upload_url',
        sessionToken,
        passcode,
        fileName: file.name,
        fileType: file.type,
        groupName,
      })) as { uploadUrl: string; fileKey: string };

      const s3Res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!s3Res.ok) throw new Error('Upload to storage failed');

      await publicFetch({
        operation: 'public_analyze_meeting',
        sessionToken,
        passcode,
        fileKey,
        numParticipants: Number(numParticipants),
        meetingPurpose,
        ...(sessionType === 'self-service' && reportEmail ? { reportEmail } : {}),
      });

      setStep('done');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Upload failed');
      setStep('form');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (selected && !isAudioFile(selected)) {
      setFile(null);
      setFileError('Seleziona un file audio (mp3, wav, m4a, …)');
      e.target.value = '';
      return;
    }
    setFileError('');
    setFile(selected);
  };

  const emailValid =
    sessionType !== 'self-service' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reportEmail);
  const canSubmit =
    step === 'form' &&
    !!file &&
    !fileError &&
    !!groupName.trim() &&
    Number(numParticipants) >= 1 &&
    !!passcode.trim() &&
    emailValid;

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
      p={2}
    >
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%' }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          ReCap Analytics
        </Typography>

        {step === 'loading' && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {step === 'closed' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Questa sessione di caricamento è chiusa.
          </Alert>
        )}

        {step === 'error' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Sessione non trovata o non valida.
          </Alert>
        )}

        {step === 'done' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Caricamento completato. L'analisi è in corso — puoi chiudere questa pagina.
          </Alert>
        )}

        {(step === 'form' || step === 'uploading') && (
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            {sessionLabel && (
              <Typography variant="subtitle1" color="text.secondary">
                {sessionLabel}
              </Typography>
            )}

            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

            <TextField
              label="Codice di accesso"
              type="password"
              required
              fullWidth
              size="small"
              value={passcode}
              disabled={step === 'uploading'}
              onChange={(e) => setPasscode(e.target.value)}
            />

            <TextField
              label="Nome del gruppo"
              required
              fullWidth
              size="small"
              value={groupName}
              disabled={step === 'uploading'}
              onChange={(e) => setGroupName(e.target.value)}
            />

            <TextField
              label="Numero di partecipanti"
              type="number"
              required
              fullWidth
              size="small"
              value={numParticipants}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={step === 'uploading'}
              onChange={(e) => setNumParticipants(e.target.value)}
            />

            <TextField
              select
              label="Scopo della riunione"
              fullWidth
              size="small"
              value={meetingPurpose}
              disabled={step === 'uploading'}
              onChange={(e) => setMeetingPurpose(e.target.value)}
            >
              {MEETING_PURPOSE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            {sessionType === 'self-service' && (
              <TextField
                label="Email per ricevere il report"
                type="email"
                required
                fullWidth
                size="small"
                value={reportEmail}
                disabled={step === 'uploading'}
                onChange={(e) => setReportEmail(e.target.value)}
              />
            )}

            <Box>
              <input
                id="upload-page-file"
                type="file"
                accept={AUDIO_ACCEPT}
                style={{ display: 'none' }}
                disabled={step === 'uploading'}
                onChange={handleFileChange}
              />
              <label htmlFor="upload-page-file">
                <Button
                  component="span"
                  variant="outlined"
                  disabled={step === 'uploading'}
                  color={fileError ? 'error' : 'primary'}
                >
                  {file ? file.name : 'Seleziona file audio *'}
                </Button>
              </label>
              {file && (
                <Chip
                  size="small"
                  label={`${(file.size / 1024 / 1024).toFixed(1)} MB`}
                  sx={{ ml: 1 }}
                />
              )}
              {fileError && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                  {fileError}
                </Typography>
              )}
              {isIOS && !file && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Su iPhone le registrazioni di Memo Vocali non compaiono qui: apri Memo Vocali,
                  tocca la registrazione → ••• → Condividi → Salva su File, poi selezionala da qui.
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={step === 'uploading'}
            >
              Carica e analizza
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
